import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Delete, Add, Block, CheckCircle } from '@mui/icons-material';
import { toast } from 'react-toastify';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}integrations/clients`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const MyIntegrations = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [createdSecret, setCreatedSecret] = useState(null);
  const [form, setForm] = useState({ name: '', scopes: 'synctrack.read', expiresAt: '' });

  const loadClients = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_BASE_URL, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to load clients');
      const data = await res.json();
      setClients(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  const createClient = async () => {
    try {
      if (!form.name.trim()) {
        toast.error('Client name is required');
        return;
      }
      const payload = {
        name: form.name.trim(),
        scopes: form.scopes.trim(),
        expiresAt: form.expiresAt ? form.expiresAt : null,
      };
      const res = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed to create client');
      const created = await res.json();
      setCreatedSecret(created);
      setOpenCreate(false);
      setForm({ name: '', scopes: 'synctrack.read', expiresAt: '' });
      await loadClients();
      toast.success('OAuth client created');
    } catch (e) {
      toast.error(e.message || 'Failed to create client');
    }
  };

  const setDisabled = async (clientId, disabled) => {
    try {
      const res = await fetch(`${API_BASE_URL}/${clientId}/disable`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ disabled }),
      });
      if (!res.ok) throw new Error('Failed to update client status');
      await loadClients();
      toast.success(disabled ? 'Client disabled' : 'Client enabled');
    } catch (e) {
      toast.error(e.message || 'Failed to update client');
    }
  };

  const deleteClient = async (clientId) => {
    if (!window.confirm('Delete this OAuth client?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/${clientId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to delete client');
      await loadClients();
      toast.success('Client deleted');
    } catch (e) {
      toast.error(e.message || 'Failed to delete client');
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">My Integrations</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setOpenCreate(true)}>
          Create Client
        </Button>
      </Stack>

      <Alert severity="warning" sx={{ mb: 2 }}>
        Client secret is shown only once at creation. Store it securely.
      </Alert>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Client ID</TableCell>
              <TableCell>Scopes</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Used</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clients.map((c) => (
              <TableRow key={c.client_id}>
                <TableCell>{c.name}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{c.client_id}</TableCell>
                <TableCell>{c.scopes || 'synctrack.read'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={c.disabled ? 'default' : 'success'}
                    label={c.disabled ? 'Disabled' : 'Active'}
                  />
                </TableCell>
                <TableCell>{c.last_used_at || '-'}</TableCell>
                <TableCell align="right">
                  {c.disabled ? (
                    <IconButton size="small" color="success" onClick={() => setDisabled(c.client_id, false)}>
                      <CheckCircle fontSize="small" />
                    </IconButton>
                  ) : (
                    <IconButton size="small" color="warning" onClick={() => setDisabled(c.client_id, true)}>
                      <Block fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton size="small" color="error" onClick={() => deleteClient(c.client_id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!loading && clients.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No OAuth clients yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create OAuth Client</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Name"
            fullWidth
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Scopes"
            fullWidth
            value={form.scopes}
            onChange={(e) => setForm((p) => ({ ...p, scopes: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Expires At (optional, yyyy-MM-dd HH:mm:ss)"
            fullWidth
            value={form.expiresAt}
            onChange={(e) => setForm((p) => ({ ...p, expiresAt: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={createClient}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!createdSecret} onClose={() => setCreatedSecret(null)} fullWidth maxWidth="sm">
        <DialogTitle>Client Secret (One-Time Display)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Save this now. You will not be able to retrieve it again.
          </Typography>
          <TextField
            margin="dense"
            label="Client ID"
            fullWidth
            value={createdSecret?.clientId || ''}
            InputProps={{ readOnly: true }}
          />
          <TextField
            margin="dense"
            label="Client Secret"
            fullWidth
            value={createdSecret?.clientSecret || ''}
            InputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setCreatedSecret(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyIntegrations;
